import React, { useRef, useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import {
  Dialog,
  Box,
  Typography,
  Button,
  Divider,
  Grid,
  Paper,
  IconButton,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  CheckCircle,
  Restaurant,
  Person,
  Receipt,
  Close,
  TableBar,
  Print,
  Sms,
  Email,
  RestaurantMenu,
} from "@mui/icons-material";
import { useUserWithCompany } from "hooks/useCompany";
import ImageLoader from "../../../../components/ImageLoader/ImageLoader";
import KitchenBill from "./KitchenBill";
import CustomerBill from "./CustomerBill";
import CompanyConfigurationServices from "../../../../services/CompanyConfigurationService";

const OrderSuccessDialog = ({
  open,
  onClose,
  orderResult,
  cashPaymentInfo,
}) => {
  const theme = useTheme();
  const {
    companyId,
    vatRate,
    taxId,
    serviceChargeRateDining,
    serviceChargeRateTakeaway,
    currency,
    company,
  } = useUserWithCompany();
  const kitchenPrintRef = useRef();
  const customerPrintRef = useRef();
  const [printSettings, setPrintSettings] = useState({
    customer: true,
    kitchen: false,
  });
  const [printerSettings, setPrinterSettings] = useState({
    enabled: true,
    copies: 2,
    paperSize: "80mm",
  });
  const [companyModules, setCompanyModules] = useState({
    POS: true,
    Kitchen: true,
  });
  const [configLoading, setConfigLoading] = useState(false);
  const [customerPrintStatus, setCustomerPrintStatus] = useState("idle");
  const [kitchenPrintStatus, setKitchenPrintStatus] = useState("idle");

  // Handle kitchen bill printing
  const handleKitchenPrint = useReactToPrint({
    contentRef: kitchenPrintRef,
    documentTitle: `Kitchen Order - ${orderResult?.orderNumber || "ORDER"}`,
  });

  // Handle customer bill printing
  const handleCustomerPrint = useReactToPrint({
    contentRef: customerPrintRef,
    documentTitle: `Receipt - ${orderResult?.orderNumber || "ORDER"}`,
  });

  const canPrintCustomer = printSettings.customer && printerSettings.enabled;
  const canPrintKitchen =
    printSettings.kitchen && printerSettings.enabled && companyModules.Kitchen;
  const printCopies = Math.max(1, Number(printerSettings.copies) || 1);

  useEffect(() => {
    if (!open || !companyId) return;

    const fetchConfigurations = async () => {
      try {
        setConfigLoading(true);
        const [printResponse, printerResponse, modulesResponse] = await Promise.all([
          CompanyConfigurationServices.getConfigurations(companyId, "print_settings"),
          CompanyConfigurationServices.getConfigurations(companyId, "printer_settings"),
          CompanyConfigurationServices.getConfigurations(companyId, "compnay_modules"),
        ]);

        const printItems =
          printResponse?.data?.view?.data ??
          printResponse?.data?.view?.items ??
          printResponse?.data?.items ??
          [];
        if (printResponse?.success && printItems.length > 0) {
          const value = printItems[0]?.value || {};
          setPrintSettings({
            customer: value.customer !== false,
            kitchen: Boolean(value.kitchen),
          });
        }

        const printerItems =
          printerResponse?.data?.view?.data ??
          printerResponse?.data?.view?.items ??
          printerResponse?.data?.items ??
          [];
        if (printerResponse?.success && printerItems.length > 0) {
          const value = printerItems[0]?.value || {};
          setPrinterSettings({
            enabled: value.enabled !== false,
            copies:
              typeof value.copies === "number" && value.copies > 0
                ? value.copies
                : 2,
            paperSize: value.paperSize || "80mm",
          });
        }

        const moduleItems =
          modulesResponse?.data?.view?.data ??
          modulesResponse?.data?.view?.items ??
          modulesResponse?.data?.items ??
          [];
        if (modulesResponse?.success && moduleItems.length > 0) {
          const value = moduleItems[0]?.value || {};
          setCompanyModules({
            POS: value.POS !== false,
            Kitchen: value.Kitchen !== false,
          });
        }
      } catch (error) {
        console.error("Error fetching POS print configurations:", error);
      } finally {
        setConfigLoading(false);
      }
    };

    fetchConfigurations();
  }, [companyId, open]);

  // Auto-print both bills when order is confirmed
  useEffect(() => {
    if (!open || !orderResult || configLoading) {
      if (!open) {
        setCustomerPrintStatus("idle");
        setKitchenPrintStatus("idle");
      }
      return;
    }

    setCustomerPrintStatus(canPrintCustomer ? "waiting" : "disabled");
    setKitchenPrintStatus(canPrintKitchen ? "waiting" : "disabled");

    if (!canPrintCustomer && !canPrintKitchen) {
      return;
    }

    const timers = [];
    let delay = 800;

    if (canPrintCustomer) {
      timers.push(
        setTimeout(() => {
          setCustomerPrintStatus("printing");
          handleCustomerPrint();
        }, delay)
      );
      timers.push(
        setTimeout(() => {
          setCustomerPrintStatus("complete");
        }, delay + 900)
      );
      delay += 1200;
    }

    if (canPrintKitchen) {
      timers.push(
        setTimeout(() => {
          setKitchenPrintStatus("printing");
          handleKitchenPrint();
        }, delay)
      );
      timers.push(
        setTimeout(() => {
          setKitchenPrintStatus("complete");
        }, delay + 900)
      );
    }

    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canPrintCustomer,
    canPrintKitchen,
    configLoading,
    open,
    orderResult?.id,
  ]);

  // Determine service charge rate based on order type
  const orderType = orderResult?.orderType || "takeaway";
  const serviceChargeRate =
    orderType === "dine-in"
      ? serviceChargeRateDining
      : serviceChargeRateTakeaway;

  console.log(
    "OrderSuccessDialog render - open:",
    open,
    "orderResult:",
    orderResult,
    "cashPaymentInfo:",
    cashPaymentInfo,
  );

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const showPrintOverlay =
    customerPrintStatus === "waiting" ||
    customerPrintStatus === "printing" ||
    kitchenPrintStatus === "waiting" ||
    kitchenPrintStatus === "printing";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: alpha(theme.palette.grey[50], 0.95),
        },
      }}
    >
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Auto-Print Overlay */}
        {showPrintOverlay && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.main} 55%, ${theme.palette.primary.dark} 100%)`,
              overflow: "hidden",
            }}
          >
            {/* Background decorations */}
            <Box
              sx={{
                position: "absolute",
                top: -100,
                right: -100,
                width: 350,
                height: 350,
                borderRadius: "50%",
                bgcolor: alpha("#fff", 0.04),
                pointerEvents: "none",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: -80,
                left: -80,
                width: 260,
                height: 260,
                borderRadius: "50%",
                bgcolor: alpha("#fff", 0.03),
                pointerEvents: "none",
              }}
            />

            <Paper
              elevation={20}
              sx={{
                p: 5,
                borderRadius: 4,
                maxWidth: 480,
                width: "90%",
                position: "relative",
                zIndex: 1,
              }}
            >
              {/* Order confirmed icon & number */}
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Box
                  sx={{
                    width: 88,
                    height: 88,
                    borderRadius: "50%",
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    border: `3px solid ${alpha(theme.palette.success.main, 0.3)}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 2.5,
                  }}
                >
                  <CheckCircle
                    sx={{ fontSize: 52, color: theme.palette.success.main }}
                  />
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
                  Order Confirmed!
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.success.main,
                    fontWeight: 700,
                    fontFamily: "monospace",
                  }}
                >
                  #{orderResult?.orderNumber?.split("-").pop()}
                </Typography>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  mb: 2.5,
                  textAlign: "center",
                  color: "text.secondary",
                }}
              >
                Sending to printers...
              </Typography>

              {/* Customer Receipt Row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  mb: 2,
                  borderRadius: 2.5,
                  bgcolor:
                    customerPrintStatus === "printing"
                      ? alpha(theme.palette.primary.main, 0.06)
                      : alpha(theme.palette.success.main, 0.05),
                  border: `1.5px solid ${customerPrintStatus === "printing"
                      ? alpha(theme.palette.primary.main, 0.25)
                      : alpha(theme.palette.success.main, 0.3)
                    }`,
                  transition: "all 0.4s ease",
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    flexShrink: 0,
                    bgcolor:
                      customerPrintStatus === "printing"
                        ? alpha(theme.palette.primary.main, 0.12)
                        : alpha(theme.palette.success.main, 0.12),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Print
                    sx={{
                      fontSize: 24,
                      color:
                        customerPrintStatus === "printing"
                          ? theme.palette.primary.main
                          : theme.palette.success.main,
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                    Customer Receipt
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 500 }}
                  >
                    {!canPrintCustomer
                      ? "Disabled by configuration"
                      : customerPrintStatus === "printing"
                        ? "Opening print dialog..."
                        : "Sent to printer ✓"}
                  </Typography>
                </Box>
                {customerPrintStatus === "printing" ? (
                  <CircularProgress
                    size={24}
                    thickness={4}
                    sx={{ color: theme.palette.primary.main }}
                  />
                ) : (
                  <CheckCircle
                    sx={{ fontSize: 28, color: theme.palette.success.main }}
                  />
                )}
              </Box>

              {/* Kitchen Order Row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  borderRadius: 2.5,
                  bgcolor:
                    kitchenPrintStatus === "printing"
                      ? alpha(theme.palette.warning.main, 0.06)
                      : alpha(theme.palette.grey[500], 0.04),
                  border: `1.5px solid ${kitchenPrintStatus === "printing"
                      ? alpha(theme.palette.warning.main, 0.3)
                      : alpha(theme.palette.grey[400], 0.2)
                    }`,
                  transition: "all 0.4s ease",
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    flexShrink: 0,
                    bgcolor:
                      kitchenPrintStatus === "printing"
                        ? alpha(theme.palette.warning.main, 0.12)
                        : alpha(theme.palette.grey[400], 0.08),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <RestaurantMenu
                    sx={{
                      fontSize: 24,
                      color:
                        kitchenPrintStatus === "printing"
                          ? theme.palette.warning.main
                          : theme.palette.grey[400],
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      color:
                        customerPrintStatus === "printing"
                          ? "text.secondary"
                          : "text.primary",
                    }}
                  >
                    Kitchen Order
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 500 }}
                  >
                    {!canPrintKitchen
                      ? companyModules.Kitchen
                        ? "Disabled by configuration"
                        : "Kitchen module disabled"
                      : customerPrintStatus === "printing"
                        ? "Waiting..."
                        : kitchenPrintStatus === "printing"
                          ? "Opening print dialog..."
                          : "Sent to printer ✓"}
                  </Typography>
                </Box>
                {customerPrintStatus === "printing" ? (
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      bgcolor: alpha(theme.palette.grey[400], 0.2),
                    }}
                  />
                ) : kitchenPrintStatus === "printing" ? (
                  <CircularProgress
                    size={24}
                    thickness={4}
                    sx={{ color: theme.palette.warning.main }}
                  />
                ) : (
                  <CheckCircle
                    sx={{ fontSize: 28, color: theme.palette.success.main }}
                  />
                )}
              </Box>
            </Paper>
          </Box>
        )}

        {/* Success Header - Compact */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
            py: 2,
            px: 3,
            position: "relative",
            boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.25)}`,
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              width: 40,
              height: 40,
              color: "#fff",
              bgcolor: alpha("#fff", 0.2),
              "&:hover": {
                bgcolor: alpha("#fff", 0.3),
                transform: "rotate(90deg)",
              },
              transition: "all 0.3s ease",
            }}
          >
            <Close sx={{ fontSize: 20 }} />
          </IconButton>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              maxWidth: 1400,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: alpha("#fff", 0.2),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CheckCircle sx={{ fontSize: 32, color: "#fff" }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  mb: 0.5,
                  fontSize: "1.25rem",
                }}
              >
                Order Placed Successfully!
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: alpha("#fff", 0.9),
                  fontWeight: 400,
                  fontSize: "0.875rem",
                }}
              >
                Your order has been confirmed and sent to the kitchen
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            py: 3,
            px: 3,
          }}
        >
          <Box sx={{ position: "relative", height: "100%" }}>
            <Grid container spacing={2.5}>
              {/* Column 1 - Order Number & Customer */}
              <Grid size={{ xs: 12, md: 2.5 }}>
                {/* Order Number Card */}
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: "#fff",
                    borderRadius: "16px",
                    p: 2.5,
                    mb: 2,
                    border: "1px solid",
                    borderColor: alpha(theme.palette.divider, 0.08),
                    boxShadow: `0 1px 3px ${alpha("#000", 0.04)}`,
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.05),
                      borderRadius: 2,
                      p: 2.5,
                      textAlign: "center",
                      border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: alpha(theme.palette.text.primary, 0.6),
                        fontWeight: 600,
                        letterSpacing: 1,
                        fontSize: "0.7rem",
                        textTransform: "uppercase",
                      }}
                    >
                      ORDER #
                    </Typography>
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 800,
                        color: theme.palette.success.main,
                        fontFamily: "monospace",
                        mt: 1,
                        fontSize: "2rem",
                        letterSpacing: 1,
                      }}
                    >
                      {orderResult?.orderNumber?.split("-").pop() || "N/A"}
                    </Typography>
                  </Box>
                </Paper>

                {/* Customer Info Card */}
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: "#fff",
                    borderRadius: "16px",
                    p: 2.5,
                    mb: 2,
                    border: "1px solid",
                    borderColor: alpha(theme.palette.divider, 0.08),
                    boxShadow: `0 1px 3px ${alpha("#000", 0.04)}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "8px",
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Person
                        sx={{ fontSize: 20, color: theme.palette.primary.main }}
                      />
                    </Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, fontSize: "1rem" }}
                    >
                      Customer Details
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: alpha(theme.palette.text.primary, 0.6),
                          fontWeight: 500,
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Name
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 700, mt: 0.5, fontSize: "0.9rem" }}
                      >
                        {orderResult?.customerName || "N/A"}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: alpha(theme.palette.text.primary, 0.6),
                          fontWeight: 500,
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Mobile
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 700, mt: 0.5, fontSize: "0.9rem" }}
                      >
                        {orderResult?.customerMobile || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                {/* Order Details Card */}
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: "#fff",
                    borderRadius: "16px",
                    p: 2.5,
                    border: "1px solid",
                    borderColor: alpha(theme.palette.divider, 0.08),
                    boxShadow: `0 1px 3px ${alpha("#000", 0.04)}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "8px",
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Restaurant
                        sx={{ fontSize: 20, color: theme.palette.primary.main }}
                      />
                    </Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, fontSize: "1rem" }}
                    >
                      Order Info
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {orderResult?.Table && (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: alpha(theme.palette.text.primary, 0.6),
                              fontWeight: 500,
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                            }}
                          >
                            Table
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <TableBar
                                sx={{
                                  fontSize: 18,
                                  color: theme.palette.primary.main,
                                }}
                              />
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 700, fontSize: "0.9rem" }}
                              >
                                {orderResult.Table.tableNumber}
                              </Typography>
                            </Box>
                            {orderResult.Table.location && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: alpha(theme.palette.text.primary, 0.6),
                                  fontSize: "0.7rem",
                                  ml: 3,
                                }}
                              >
                                {orderResult.Table.location}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Divider />
                      </>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: alpha(theme.palette.text.primary, 0.6),
                          fontWeight: 500,
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                        }}
                      >
                        Type
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 700,
                          textTransform: "capitalize",
                          fontSize: "0.9rem",
                        }}
                      >
                        {orderResult?.orderType || "N/A"}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        minHeight: 32,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: alpha(theme.palette.text.primary, 0.6),
                          fontWeight: 500,
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                        }}
                      >
                        Status
                      </Typography>
                      <Chip
                        label={orderResult?.status?.toUpperCase() || "N/A"}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(theme.palette.warning.main, 0.15),
                          color: theme.palette.warning.main,
                          height: 24,
                          fontSize: "0.75rem",
                          border: "none",
                        }}
                      />
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        minHeight: 32,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: alpha(theme.palette.text.primary, 0.6),
                          fontWeight: 500,
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                        }}
                      >
                        Time
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                      >
                        {formatDate(orderResult?.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Column 2 - Order Items */}
              <Grid size={{ xs: 12, md: 4.5 }}>
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: "#fff",
                    borderRadius: "16px",
                    p: 2.5,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid",
                    borderColor: alpha(theme.palette.divider, 0.08),
                    boxShadow: `0 1px 3px ${alpha("#000", 0.04)}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "8px",
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Receipt
                        sx={{ fontSize: 20, color: theme.palette.primary.main }}
                      />
                    </Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, fontSize: "1rem" }}
                    >
                      Order Items ({orderResult?.OrderItems?.length || 0})
                    </Typography>
                  </Box>

                  {/* Items List */}
                  <Box sx={{ flex: 1, overflow: "auto", pr: 0.5 }}>
                    {orderResult?.OrderItems &&
                      orderResult.OrderItems.length > 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        {orderResult.OrderItems.map((item, idx) => (
                          <Paper
                            key={idx}
                            elevation={0}
                            sx={{
                              p: 2,
                              bgcolor: item.offerId
                                ? alpha(theme.palette.info.main, 0.03)
                                : alpha(theme.palette.grey[50], 0.5),
                              borderRadius: "12px",
                              border: "1px solid",
                              borderColor: item.offerId
                                ? alpha(theme.palette.info.main, 0.15)
                                : alpha(theme.palette.divider, 0.08),
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                mb: 1,
                              }}
                            >
                              {/* Item Image */}
                              <Box
                                sx={{
                                  width: 70,
                                  height: 70,
                                  flexShrink: 0,
                                  borderRadius: 2,
                                  overflow: "hidden",
                                  bgcolor: alpha(theme.palette.grey[300], 0.2),
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {item.MenuItem?.image ? (
                                  <ImageLoader
                                    imagePath={item.MenuItem.image}
                                    width={70}
                                    height={70}
                                    alt={item.MenuItem?.name || "Item"}
                                    borderRadius={2}
                                  />
                                ) : (
                                  <Restaurant
                                    sx={{
                                      fontSize: 32,
                                      color: alpha(
                                        theme.palette.grey[400],
                                        0.5,
                                      ),
                                    }}
                                  />
                                )}
                              </Box>

                              {/* Item Details */}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: 0.5,
                                  }}
                                >
                                  <Chip
                                    label={`${item.quantity}x`}
                                    size="small"
                                    sx={{
                                      fontWeight: 700,
                                      bgcolor: theme.palette.primary.main,
                                      color: "#fff",
                                      height: 24,
                                      fontSize: "0.75rem",
                                      minWidth: 42,
                                      border: "none",
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    {item.MenuItem?.name || "Item"}
                                  </Typography>
                                  {item.offerId && (
                                    <Chip
                                      label="Combo"
                                      size="small"
                                      sx={{
                                        height: 18,
                                        fontSize: "0.65rem",
                                        fontWeight: 700,
                                        bgcolor: alpha(
                                          theme.palette.info.main,
                                          0.15,
                                        ),
                                        color: theme.palette.info.main,
                                        border: "none",
                                      }}
                                    />
                                  )}
                                </Box>
                                {item.ItemVariation && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: alpha(
                                        theme.palette.text.primary,
                                        0.6,
                                      ),
                                      fontSize: "0.75rem",
                                      display: "block",
                                      mb: 0.5,
                                    }}
                                  >
                                    Variation: {item.ItemVariation.name}
                                  </Typography>
                                )}
                                {item.OrderItemModifiers &&
                                  item.OrderItemModifiers.length > 0 && (
                                    <Box sx={{ mt: 0.5 }}>
                                      {item.OrderItemModifiers.map(
                                        (modifier, modIdx) => (
                                          <Typography
                                            key={modIdx}
                                            variant="caption"
                                            sx={{
                                              color: alpha(
                                                theme.palette.text.primary,
                                                0.6,
                                              ),
                                              fontSize: "0.7rem",
                                              display: "block",
                                            }}
                                          >
                                            + {modifier.ModifierOption?.name} (
                                            {currency}{" "}
                                            {parseFloat(modifier.price).toFixed(
                                              2,
                                            )}
                                            )
                                          </Typography>
                                        ),
                                      )}
                                    </Box>
                                  )}
                                {item.specialInstructions && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: alpha(
                                        theme.palette.warning.main,
                                        0.8,
                                      ),
                                      fontSize: "0.7rem",
                                      fontStyle: "italic",
                                      display: "block",
                                      mt: 0.5,
                                    }}
                                  >
                                    Note: {item.specialInstructions}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: alpha(theme.palette.text.primary, 0.6),
                                  fontWeight: 500,
                                  fontSize: "0.75rem",
                                }}
                              >
                                {item.OrderItemModifiers &&
                                  item.OrderItemModifiers.length > 0
                                  ? "Total Price"
                                  : "Price"}
                              </Typography>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  color: theme.palette.primary.main,
                                  fontSize: "1rem",
                                }}
                              >
                                {currency}{" "}
                                {parseFloat(item.totalPrice).toFixed(2)}
                              </Typography>
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          textAlign: "center",
                          py: 4,
                        }}
                      >
                        No items found
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* Column 3 - Payment Summary */}
              <Grid size={{ xs: 12, md: 3 }}>
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: "#fff",
                    borderRadius: "16px",
                    p: 2.5,
                    border: "1px solid",
                    borderColor: alpha(theme.palette.divider, 0.08),
                    boxShadow: `0 1px 3px ${alpha("#000", 0.04)}`,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, mb: 3, fontSize: "1.125rem" }}
                  >
                    Payment Summary
                  </Typography>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: alpha(theme.palette.text.primary, 0.6),
                          fontSize: "0.875rem",
                        }}
                      >
                        Subtotal
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 700, fontSize: "0.9rem" }}
                      >
                        {currency}{" "}
                        {parseFloat(orderResult?.subtotal || 0).toFixed(2)}
                      </Typography>
                    </Box>

                    {parseFloat(orderResult?.discountAmount || 0) > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.success.main,
                          }}
                        >
                          Discount
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 700,
                            color: theme.palette.success.main,
                          }}
                        >
                          - {currency}{" "}
                          {parseFloat(orderResult.discountAmount).toFixed(2)}
                        </Typography>
                      </Box>
                    )}

                    {/* Service Charge */}
                    {parseFloat(orderResult?.serviceChargeAmount || 0) > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                          }}
                        >
                          Service Charge ({serviceChargeRate}%)
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                          {currency}{" "}
                          {parseFloat(
                            orderResult?.serviceChargeAmount || 0,
                          ).toFixed(2)}
                        </Typography>
                      </Box>
                    )}

                    {/* VAT */}
                    {parseFloat(orderResult?.vatAmount || 0) > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                          }}
                        >
                          VAT ({vatRate}%) {taxId && `- ${taxId}`}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                          {currency}{" "}
                          {parseFloat(orderResult?.vatAmount || 0).toFixed(2)}
                        </Typography>
                      </Box>
                    )}

                    <Divider sx={{ my: 1 }} />

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 2.5,
                        bgcolor: alpha(theme.palette.success.main, 0.05),
                        borderRadius: 2,
                        border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, fontSize: "1.125rem" }}
                      >
                        Total
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 800,
                          color: theme.palette.success.main,
                          fontSize: "1.5rem",
                        }}
                      >
                        {currency}{" "}
                        {parseFloat(orderResult?.totalAmount || 0).toFixed(2)}
                      </Typography>
                    </Box>

                    {/* Cash Payment Info */}
                    {cashPaymentInfo && (
                      <Box sx={{ mt: 3 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, mb: 2, fontSize: "0.875rem" }}
                        >
                          Cash Payment Details
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              p: 1.5,
                              bgcolor: alpha(theme.palette.info.main, 0.05),
                              borderRadius: 1.5,
                              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                            >
                              Cash Received
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 700, fontSize: "0.9rem" }}
                            >
                              {currency}{" "}
                              {cashPaymentInfo.cashReceived.toFixed(2)}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              p: 1.5,
                              bgcolor: alpha(theme.palette.warning.main, 0.05),
                              borderRadius: 1.5,
                              border: `2px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 700, fontSize: "0.9rem" }}
                            >
                              Change to Return
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 800,
                                color: theme.palette.warning.main,
                                fontSize: "1.125rem",
                              }}
                            >
                              {currency}{" "}
                              {cashPaymentInfo.changeAmount.toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* Column 4 - Actions */}
              <Grid size={{ xs: 12, md: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    height: "100%",
                  }}
                >
                  {/* Actions Card */}
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: "16px",
                      border: "1px solid",
                      borderColor: alpha(theme.palette.divider, 0.08),
                      boxShadow: `0 1px 3px ${alpha("#000", 0.04)}`,
                      overflow: "hidden",
                    }}
                  >
                    {/* Card Header */}
                    <Box
                      sx={{
                        px: 2.5,
                        py: 1.75,
                        bgcolor: alpha(theme.palette.grey[900], 0.03),
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.07)}`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          letterSpacing: 1,
                          textTransform: "uppercase",
                          color: theme.palette.text.secondary,
                        }}
                      >
                        Actions
                      </Typography>
                    </Box>

                    {/* Action Rows */}
                    {[
                      {
                        icon: <Print sx={{ fontSize: 20 }} />,
                        label: "Print Customer Bill",
                        sub: canPrintCustomer
                          ? `${printerSettings.paperSize} thermal receipt`
                          : printerSettings.enabled
                            ? "Disabled in print settings"
                            : "Printer disabled",
                        color: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        onClick: () => {
                          handleCustomerPrint();
                          toast.success("Printing customer bill...");
                        },
                        disabled: !canPrintCustomer,
                      },
                      {
                        icon: <RestaurantMenu sx={{ fontSize: 20 }} />,
                        label: "Print Kitchen Order",
                        sub: canPrintKitchen
                          ? `${printerSettings.paperSize} kitchen ticket`
                          : !companyModules.Kitchen
                            ? "Kitchen module disabled"
                            : printerSettings.enabled
                              ? "Disabled in print settings"
                              : "Printer disabled",
                        color: theme.palette.warning.dark,
                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                        onClick: () => {
                          handleKitchenPrint();
                          toast.success("Printing kitchen bill...");
                        },
                        disabled: !canPrintKitchen,
                      },
                      {
                        icon: <Sms sx={{ fontSize: 20 }} />,
                        label: "Send SMS",
                        sub: orderResult?.customerMobile || "No number on file",
                        color: theme.palette.success.dark,
                        bgcolor: alpha(theme.palette.success.main, 0.08),
                        onClick: () =>
                          toast.success(
                            `Bill sent via SMS to ${orderResult?.customerMobile}`,
                          ),
                        disabled: !orderResult?.customerMobile,
                      },
                      {
                        icon: <Email sx={{ fontSize: 20 }} />,
                        label: "Send Email",
                        sub: "Digital receipt",
                        color: theme.palette.info.dark,
                        bgcolor: alpha(theme.palette.info.main, 0.08),
                        onClick: () => toast.success("Bill sent via email"),
                        disabled: false,
                      },
                    ].map((action, idx, arr) => (
                      <Box
                        key={idx}
                        onClick={action.disabled ? undefined : action.onClick}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          px: 2,
                          py: 1.5,
                          cursor: action.disabled ? "not-allowed" : "pointer",
                          opacity: action.disabled ? 0.4 : 1,
                          bgcolor: "white",
                          borderBottom:
                            idx < arr.length - 1
                              ? `1px solid ${alpha(theme.palette.divider, 0.06)}`
                              : "none",
                          transition: "background 0.18s ease",
                          "&:hover": action.disabled
                            ? {}
                            : {
                              bgcolor: alpha(action.color, 0.04),
                            },
                        }}
                      >
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: "10px",
                            flexShrink: 0,
                            bgcolor: action.bgcolor,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: action.color,
                          }}
                        >
                          {action.icon}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.825rem",
                              lineHeight: 1.3,
                              color: "text.primary",
                            }}
                          >
                            {action.label}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.7rem",
                              color: "text.secondary",
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {action.sub}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            bgcolor: action.disabled
                              ? "transparent"
                              : alpha(action.color, 0.5),
                            flexShrink: 0,
                          }}
                        />
                      </Box>
                    ))}
                  </Paper>

                  {/* Next Customer Button */}
                  <Button
                    variant="contained"
                    size="large"
                    onClick={onClose}
                    sx={{
                      flex: 1,
                      minHeight: 120,
                      fontWeight: 800,
                      fontSize: "1.1rem",
                      borderRadius: "16px",
                      textTransform: "none",
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      color: "#fff",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1.5,
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.35)}`,
                      border: `1px solid ${alpha(theme.palette.primary.light, 0.2)}`,
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "50%",
                        background: alpha("#fff", 0.06),
                        borderRadius: "16px 16px 60% 60%",
                        pointerEvents: "none",
                      },
                      "&:hover": {
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                        transform: "translateY(-3px)",
                        boxShadow: `0 10px 28px ${alpha(theme.palette.primary.main, 0.45)}`,
                      },
                      "&:active": { transform: "translateY(-1px)" },
                      transition: "all 0.25s ease",
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        bgcolor: alpha("#fff", 0.15),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.5rem",
                      }}
                    >
                      →
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: "1rem",
                        lineHeight: 1.2,
                      }}
                    >
                      Next Customer
                    </Typography>
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>

      {/* Hidden Customer Bill for Printing */}
      <Box sx={{ display: "none" }}>
        <Box ref={customerPrintRef}>
          {Array.from({ length: printCopies }).map((_, index) => (
            <Box
              key={`customer-copy-${index}`}
              sx={{
                pageBreakAfter: index < printCopies - 1 ? "always" : "auto",
              }}
            >
              <CustomerBill
                orderResult={orderResult}
                companyInfo={company}
                currency={currency}
                paperSize={printerSettings.paperSize}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Hidden Kitchen Bill for Printing */}
      <Box sx={{ display: "none" }}>
        <Box ref={kitchenPrintRef}>
          {Array.from({ length: printCopies }).map((_, index) => (
            <Box
              key={`kitchen-copy-${index}`}
              sx={{
                pageBreakAfter: index < printCopies - 1 ? "always" : "auto",
              }}
            >
              <KitchenBill
                orderResult={orderResult}
                companyInfo={company}
                paperSize={printerSettings.paperSize}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Dialog>
  );
};

export default OrderSuccessDialog;
